import type { SeoPageConfig } from '../types/Seo';
import { seoTitle } from '../seo/site';

export type MarketingArticleSection = {
  id: string;
  eyebrow?: string;
  title: string;
  paragraphs: string[];
};

export type MarketingArticleComparisonRow = {
  aspect: string;
  other: string;
  preztimer: string;
};

export type MarketingArticleModel = {
  seo: SeoPageConfig;
  heroEyebrow: string;
  sections: MarketingArticleSection[];
  /** Таблица сравнения — только для `/compare/...`. */
  comparison?: MarketingArticleComparisonRow[];
  /** Дополнительные пункты в блоке «Дальше». */
  extraNextLinks?: readonly { to: string; label: string }[];
};

const seo = (
  title: string,
  description: string,
  h1: string,
  summary: string,
  path: string,
): SeoPageConfig => ({
  title: seoTitle(title),
  description,
  h1,
  summary,
  path,
});

export const MARKETING_ARTICLE_PAGES: MarketingArticleModel[] = [
  {
    seo: seo(
      'Таймер для конференции',
      'Как вести тайминг докладов и секций: одна комната, панель управления и экран таймера для спикера в браузере — без отдельной установки.',
      'Таймер для конференции: панель и экран для спикера',
      'Одна комната на мероприятие: оператор ведёт слоты, спикер видит крупный отсчёт и короткие сигналы на отдельной ссылке.',
      '/guides/timer-dlya-konferentsii',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/use-cases/conferences', label: 'Сценарий «Конференции»: программа, зал и панель' },
      {
        to: '/use-cases/presentations',
        label: 'Сценарий «Презентации и доклады»: один экран у спикера',
      },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Что должно работать на площадке',
        paragraphs: [
          'На конференции тайминг держит не один человек: продюсер, модератор зала и сами спикеры. Если отсчёт виден только на мониторе оператора, спикер узнаёт о перерасходе с задержкой — программа сдвигается.',
          'Нужен общий ориентир по времени: крупные цифры на экране у спикера и синхронное управление с панели — пауза, продление, переход к следующему блоку.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как устроен preztimer',
        title: 'Комната, панель и экран таймера',
        paragraphs: [
          'Создайте комнату и добавьте таймеры под доклады или секции. Панель управления — для команды: старт, пауза, сброс, сообщения на экран.',
          'Спикеру отдайте ссылку на экран таймера или QR из окна шаринга на панели. Экран открывается в браузере на ноутбуке, планшете или на выходе видео в зал.',
          'Лицензия Базовый подходит для старта с лимитами по комнатам и подключениям; Профи — когда нужны команда и больший объём таймеров в комнате. Смена лицензии — в разделе «Аккаунт».',
          'Шаги «создайте комнату — поделитесь ссылками — ведите программу» и ответы по залу разобраны на странице сценария «Конференции»; здесь — компактно о связке панели и экрана на площадке.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Как контролировать тайминг мероприятия',
      'Пошагово: одна комната на событие, таймеры по программе, экран для спикера и ручное управление без автозапуска по расписанию.',
      'Как контролировать тайминг мероприятия',
      'Старт и пауза — с панели управления; все подключённые экраны получают одно и то же состояние в реальном времени.',
      '/guides/kak-kontrolirovat-tajming',
    ),
    heroEyebrow: 'Гайд',
    sections: [
      {
        id: 'before',
        eyebrow: 'До эфира',
        title: 'Подготовьте программу в комнате',
        paragraphs: [
          'Заведите таймеры под выступления или секции с понятными названиями — так на панели и на экране таймера меньше путаницы в движении.',
          'Проверьте ссылку на экран таймера на том устройстве, которое пойдёт в зал или в сцену: масштаб, полноэкранный режим, отдельный монитор.',
        ],
      },
      {
        id: 'during',
        eyebrow: 'Во время',
        title: 'Один оператор — одна точка правды по времени',
        paragraphs: [
          'Запускайте и останавливайте таймер только с панели управления: так у спикера и команды не расходятся показания.',
          'Если блок уходит в перерасход, на экране таймера видно время после нуля — модератору проще мягко завершить секцию без споров о цифрах.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Режимы таймера: отсчёт и часы',
      'Обратный отсчёт, прямой отсчёт и часы в комнате preztimer: как режим попадает на экран таймера и что выбрать под регламент.',
      'Режимы таймера: обратный и прямой отсчёт, часы',
      'Режим задаётся на каждом таймере в панели управления; экран таймера повторяет состояние комнаты без отдельной настройки.',
      '/guides/timer-rezhimy-sekundomer-i-chasy',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/use-cases/conferences', label: 'Сценарий «Конференции»: программа и зал' },
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
      { to: '/guides/timer-vmesto-vidzheta-na-sajte', label: 'Таймер на сайте: ссылка вместо iframe' },
      { to: '/features', label: 'Таблица возможностей и лимиты лицензий' },
      { to: '/guides/kak-kontrolirovat-tajming', label: 'Как вести тайминг в эфире' },
    ],
    sections: [
      {
        id: 'modes',
        eyebrow: 'Режимы',
        title: 'Три способа показать время на слоте',
        paragraphs: [
          'Обратный отсчёт — от заданной длительности к нулю: удобно, когда у блока жёсткий лимит. Прямой отсчёт — время после старта без фиксированного конца, например для открытой дискуссии. Часы — текущее время площадки в выбранном формате.',
          'Цветовые зоны и оверран после нуля работают вместе с режимом: спикер и зал видят одну и ту же шкалу.',
        ],
      },
      {
        id: 'viewer',
        eyebrow: 'Экран',
        title: 'Один URL для спикера и вывода в зал',
        paragraphs: [
          'Экран таймера открывают по ссылке из комнаты или по QR: на ноутбуке у спикера, на планшете у модератора или в окне браузера в трансляции. Запуск и пауза остаются на панели управления — экран только отображает состояние.',
          'Для конференции обычно несколько таймеров подряд в одной комнате; для одной презентации часто хватает одного таймера на весь слот.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер на сайте без виджета: ссылка на экран',
      'У preztimer нет встраивания отсчёта в чужой сайт через iframe: ссылка на экран таймера, полноэкранное окно или вывод в трансляцию — без публичного API.',
      'Таймер на сайте: ссылка на экран вместо iframe',
      'Панель управления и комната остаются на preztimer; на площадке или у зрителя открывают только экран с крупным временем.',
      '/guides/timer-vmesto-vidzheta-na-sajte',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
      { to: '/guides/timer-rezhimy-sekundomer-i-chasy', label: 'Режимы отсчёта и часы' },
      { to: '/guides/kak-kontrolirovat-tajming', label: 'Как контролировать тайминг в эфире' },
    ],
    sections: [
      {
        id: 'why',
        eyebrow: 'Задача',
        title: 'Нужен отсчёт рядом с контентом, а не отдельная вкладка',
        paragraphs: [
          'Сервисы с «таймером для сайта» часто предлагают iframe или конструктор блока, который встраивают в вёрстку страницы. Это удобно маркетологу, но не решает задачу сцены: там нужен крупный сигнал, синхрон с командой и ручной старт без правки кода сайта.',
          'preztimer заточен под мероприятие: одна комната, панель управления и отдельная ссылка на экран таймера. Встраивания произвольной страницы сервиса в чужой домен через iframe нет — это сознательное ограничение, а не «временная дыра».',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как сделать без виджета',
        title: 'Ссылка, QR и отдельное окно',
        paragraphs: [
          'Создайте комнату, настройте таймер и откройте экран таймера по ссылке из панели или по QR: в полноэкранном окне браузера, на втором мониторе, в источнике OBS или на устройстве у ведущего. Сайт мероприятия может вести на лендинг с регистрацией, а отсчёт — на отдельной вкладке или экране площадки.',
          'Управление временем остаётся у тех, у кого есть доступ к панели управления; зрителю или гостю сайта не нужен доступ к комнате, если вы не делитесь панелью.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер для квиза и настолок',
      'Квиз и игры в зале: один отсчёт на экране для команд, ручной старт раундов с панели управления — без игрового движка и без отдельного приложения.',
      'Таймер для квиза и игр: комната и экран',
      'preztimer не ведёт правила игры и не принимает ответы: даёт синхронный тайминг и короткие сигналы на экран таймера.',
      '/guides/timer-dlya-kviza-i-igr',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/guides/timer-rezhimy-sekundomer-i-chasy', label: 'Режимы отсчёта и часы' },
      { to: '/guides/kak-kontrolirovat-tajming', label: 'Как контролировать тайминг в эфире' },
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Общий таймер для зала и ведущего',
        paragraphs: [
          'В квизе и настолках время раунда должно видеть и команда у стола, и ведущий, и зрители — иначе спорят о продлении или досрочном финале. Телефоны у участников не заменяют общий сигнал в зале.',
          'Нужен один источник правды по секундам: крупные цифры на отдельном экране и человек, который стартует и ставит на паузу с панели управления.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как устроить на preztimer',
        title: 'Комната, раунды и сообщения ведущему',
        paragraphs: [
          'Создайте комнату и заведите таймер под каждый раунд или часть вечера — порядок виден на панели. Включите обратный отсчёт для лимита на ответы или прямой отсчёт, если блок без жёсткого конца; переключение следующего таймера — вручную с панели.',
          'Экран таймера откройте на телевизоре в зале или на планшете у ведущего по ссылке или QR. Короткие подсказки («30 секунд», «стоп игра») выводите сообщениями с панели — они появляются на экране таймера синхронно для всех.',
          'Сервис не проверяет ответы и не ведёт счёт очков: правила, бланки и античит остаются на вашей стороне; preztimer держит только тайминг и сигналы на экран.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер для совещания и панели',
      'Очередь выступающих на совещании: один оператор ведёт таймеры с панели, участники видят экран таймера в браузере — без отдельного приложения.',
      'Таймер для совещания: очередь и лимит слота',
      'Ручной старт и пауза с панели управления; переход к следующему таймеру — тоже вручную, без автозапуска по расписанию.',
      '/guides/timer-dlya-soveschaniya',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
      { to: '/guides/kak-kontrolirovat-tajming', label: 'Как контролировать тайминг в эфире' },
      { to: '/guides/timer-rezhimy-sekundomer-i-chasy', label: 'Режимы отсчёта и часы' },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Один регламент на комнату, а не личные таймеры',
        paragraphs: [
          'На планёрке или панели у каждого свой таймер на телефоне — спорят о том, когда закончился слот. Нужен общий отсчёт на экране в переговорке и человек, который продлевает или переключает блок.',
          'preztimer не заменяет календарь и не ведёт протокол: даёт синхронный тайминг и короткие сигналы на экран таймера для ведущего и зала.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как настроить',
        title: 'Таймеры под докладчиков и переходы',
        paragraphs: [
          'Создайте комнату и заведите таймер на каждого спикера или на секцию повестки. Модератор или секретарь открывает панель управления; участникам — только ссылка на экран таймера на мониторе в комнате или в общей трансляции.',
          'Сообщения вроде «минута на выводы» или «переходим к голосованию» отправляйте с панели — они появляются на экране таймера без шума в общем чате.',
          'Лимиты комнаты по таймерам и карточкам сообщений зависят от лицензии — см. таблицу на странице возможностей и раздел «Цены».',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер для выставки и демо-зоны',
      'Слоты на стенде и демо: панель у команды, экран таймера для посетителей — очередь без спора о минутах. Один отсчёт на мониторе у зоны.',
      'Таймер для выставки: слоты на стенде',
      'Один отсчёт на мониторе у зоны; оператор переключает раунды с панели управления в браузере.',
      '/guides/timer-dlya-vystavki',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/use-cases/conferences', label: 'Сценарий «Конференции»: программа и зал' },
      { to: '/guides/kak-kontrolirovat-tajming', label: 'Как контролировать тайминг в эфире' },
      { to: '/guides/timer-dlya-soveschaniya', label: 'Таймер для совещания и панели' },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Демо по графику без срыва очереди',
        paragraphs: [
          'На выставке посетитель тянет время, пока менеджер не покажет цифры; следующая группа стоит в очереди без понимания, сколько осталось слоту.',
          'Нужен крупный таймер у стойки или на планшете у хоста и единая точка управления — старт, пауза, переход к следующему блоку.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как устроить',
        title: 'Комната на стенд и экран в зоне',
        paragraphs: [
          'Создайте комнату на день или на серию демо, добавьте таймеры под стандартные слоты. Экран таймера откройте на дисплее у зоны или на планшете у ведущего по ссылке или QR.',
          'Команда на стенде ведёт панель управления: при срыве графика — пауза или продление без перенастройки железа у посетителя.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер для эфира, студии и записи',
      'Сегменты прямого эфира и записи: панель у режиссёра, экран таймера у ведущего или в сцене — синхронно и вручную с панели.',
      'Таймер для эфира и студии: сегменты по времени',
      'Нет автозапуска по сетке вещания из файла: старт и переходы блоков — с панели управления; зрители не видят панель, только эфир.',
      '/guides/timer-dlya-efira',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/use-cases/webinar-timer', label: 'Таймер для вебинара' },
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
      { to: '/compare/preztimer-vs-stagetimer', label: 'Сравнение с Stagetimer' },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Жёсткий тайминг блоков в кадре',
        paragraphs: [
          'В студии и на стриме блоки сменяются по минутам: ведущий, гости и плейаут должны видеть один и тот же остаток, иначе речь обрывается или уходит в перерасход.',
          'Отдельный экран таймера снимает путаницу между таймером в плейауте и личным телефоном у столика.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как подключить',
        title: 'Панель, эфир и отдельная ссылка',
        paragraphs: [
          'Создайте комнату, разбейте эфир на таймеры по сегментам. Режиссёр или продюсер держит панель управления; ведущий и оператор открывают экран таймера на отдельном мониторе или в окне браузера в OBS.',
          'Для службы с длинным регламентом или записи с паузами используйте те же приёмы: ручной старт и переход к следующему таймеру, сообщения на экран для сигналов в кадр.',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер для фитнеса и интервалов',
      'Раунды и отдых в зале: таймеры под секции тренировки, экран у зоны — старт с панели, без отдельного приложения на телевизоре.',
      'Таймер для фитнеса: раунды и отдых',
      'Обратный отсчёт на рабочую фазу и на отдых задаётся на каждом таймере; переключение раунда — вручную с панели управления.',
      '/guides/timer-dlya-fitnesa',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/guides/timer-rezhimy-sekundomer-i-chasy', label: 'Режимы отсчёта и часы' },
      { to: '/guides/timer-dlya-kviza-i-igr', label: 'Таймер для квиза и игр' },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Интервалы видны группе целиком',
        paragraphs: [
          'В HIIT и круговых тренировках участникам нужен крупный остаток секунд у зоны, а не только голос тренера.',
          'Тренер держит панель управления и запускает фазы; на экране таймера зал видит тот же отсчёт, что и у разметки.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как собрать',
        title: 'Таймеры под раунды и отдых',
        paragraphs: [
          'Заведите отдельные таймеры под рабочую фазу и отдых или под весь круг из нескольких блоков — порядок на панели совпадает с планом тренировки. Режим обратного отсчёта задаётся на каждом таймере.',
          'Экран таймера выведите на телевизор в зале или на планшет у зоны по ссылке или QR. Лимиты комнаты по числу таймеров зависят от лицензии — см. страницу возможностей и «Цены».',
        ],
      },
    ],
  },
  {
    seo: seo(
      'Таймер для школы и вуза',
      'Урок и экзамен: панель у преподавателя, экран таймера в аудитории — общий регламент секций без отдельного приложения на доске.',
      'Таймер для образования: урок и экзамен',
      'Старт и пауза с панели управления; экран показывает остаток времени на часть урока или на задание.',
      '/guides/timer-dlya-obrazovaniya',
    ),
    heroEyebrow: 'Гайд',
    extraNextLinks: [
      { to: '/guides/timer-dlya-soveschaniya', label: 'Таймер для совещания и панели' },
      { to: '/guides/timer-rezhimy-sekundomer-i-chasy', label: 'Режимы отсчёта и часы' },
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
    ],
    sections: [
      {
        id: 'task',
        eyebrow: 'Задача',
        title: 'Общий лимит времени на секцию',
        paragraphs: [
          'На уроке и на экзамене важно, чтобы класс видел один остаток минут на часть занятия, а не разные приложения на телефонах.',
          'preztimer не ведёт журнал оценок и не проверяет ответы: даёт только синхронный тайминг и короткие сигналы на экран таймера.',
        ],
      },
      {
        id: 'how',
        eyebrow: 'Как использовать',
        title: 'Комната на занятие и экран в аудитории',
        paragraphs: [
          'Создайте комнату на урок или на экзаменационный блок, добавьте таймеры под этапы: вступление, самостоятельная работа, сбор ответов. Преподаватель открывает панель управления; на проектор или интерактивную панель — экран таймера по ссылке.',
          'Сообщения вроде «осталось две минуты» или «сдавайте работы» выводите с панели — они появляются на экране таймера у аудитории.',
          'Лимиты Базовый и Профи на комнаты, таймеры и подключения — в таблице на странице возможностей и в разделе «Цены».',
        ],
      },
    ],
  },
  {
    seo: seo(
      'preztimer и Stagetimer',
      'Сравнение по сценарию: отдельное приложение под телесуфлёр и таймеры против браузерной комнаты с панелью, экраном для спикера и командой.',
      'preztimer и Stagetimer: в чём разница по сценарию',
      'Оба инструмента помогают держать время выступления; отличается способ подключения к эфиру и состав команды на площадке.',
      '/compare/preztimer-vs-stagetimer',
    ),
    heroEyebrow: 'Сравнение',
    sections: [
      {
        id: 'context',
        eyebrow: 'Контекст',
        title: 'Разные точки входа в тайминг',
        paragraphs: [
          'Stagetimer — отдельное приложение, которое часто ставят на устройство у спикера или оператора и настраивают таймеры локально или по ссылке продукта.',
          'preztimer — веб-сервис: комната в браузере, панель управления для команды и отдельная ссылка на экран таймера для спикера или вывода в зал.',
        ],
      },
    ],
    extraNextLinks: [
      { to: '/use-cases/conferences', label: 'Сценарий «Конференции»: программа и зал' },
      { to: '/use-cases/presentations', label: 'Сценарий «Презентации и доклады»' },
    ],
    comparison: [
      {
        aspect: 'Установка',
        other: 'Приложение на устройство (в зависимости от платформы и версии).',
        preztimer: 'Браузер на компьютере или планшете, без отдельного установочного пакета.',
      },
      {
        aspect: 'Где сценарий и вывод в эфир',
        other:
          'Таймеры в приложении на устройстве; в трансляцию — через окно программы или зеркалирование экрана.',
        preztimer:
          'Комната в браузере: программа в preztimer, панель управления и экран таймера по отдельным ссылкам.',
      },
      {
        aspect: 'Команда на площадке',
        other: 'Фокус на одном или нескольких таймерах и подсказках в интерфейсе продукта.',
        preztimer: 'Панель управления и экран таймера разделены: оператор и спикер могут сидеть на разных машинах.',
      },
      {
        aspect: 'Интеграция с трансляцией',
        other: 'Вывод через окно приложения или зеркалирование экрана — по настройке пользователя.',
        preztimer: 'Ссылка на экран таймера в отдельном окне браузера или в источнике OBS.',
      },
    ],
  },
  {
    seo: seo(
      'preztimer и таймер в PowerPoint',
      'Таймер в режиме докладчика PowerPoint и отдельный экран таймера в браузере: когда удобнее вынести отсчёт на второй монитор или вещание.',
      'preztimer и таймер PowerPoint: что выбрать для сцены',
      'В PowerPoint таймер привязан к презентации; preztimer ведёт слоты независимо от слайдов и даёт общий экран для ведущего или зала.',
      '/compare/preztimer-vs-powerpoint-timer',
    ),
    heroEyebrow: 'Сравнение',
    sections: [
      {
        id: 'context',
        eyebrow: 'Контекст',
        title: 'Два способа показать время спикеру',
        paragraphs: [
          'В PowerPoint таймер докладчика виден там, где открыт режим докладчика — обычно на ноутбуке спикера рядом со слайдами.',
          'preztimer выносит крупный отсчёт на отдельный URL: его открывают на втором мониторе, планшете у ведущего или в сцене трансляции без привязки к файлу .pptx.',
        ],
      },
    ],
    comparison: [
      {
        aspect: 'Привязка к слайдам',
        other: 'Таймер идёт в контексте текущей презентации.',
        preztimer: 'Таймеры ведутся в комнате: подходят для череды спикеров и блоков без одного файла слайдов.',
      },
      {
        aspect: 'Сообщения на экран',
        other: 'Зависит от сценария: заметки докладчика не совпадают с «сигналом в зал».',
        preztimer: 'Короткие сообщения на экран таймера готовит команда с панели управления.',
      },
      {
        aspect: 'Несколько спикеров подряд',
        other: 'Смена спикера часто означает смену файла или ручной перенастрой.',
        preztimer: 'Один поток таймеров в комнате: оператор переключает слоты по программе.',
      },
    ],
  },
];

export function getMarketingArticleByPath(pathname: string): MarketingArticleModel | undefined {
  const normalized = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  return MARKETING_ARTICLE_PAGES.find((p) => p.seo.path === normalized);
}

export const SEO_MARKETING_ARTICLES: SeoPageConfig[] = MARKETING_ARTICLE_PAGES.map((p) => p.seo);
